import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreditCard, Plus, Trash2 } from 'lucide-react';

export const ExpenseSplitPaymentsEditor = ({
  payments = [],
  paymentMethods = [],
  totalAmount = 0,
  onAddPayment,
  onRemovePayment,
  onPaymentChange,
  title = 'Formas de pagamento',
  description = 'Divida o valor por múltiplos métodos sempre que necessário.',
  testIdPrefix = 'expense-split-payments',
}) => {
  const normalizedTotal = Number(totalAmount || 0);
  const totalPaid = payments.reduce((sum, payment) => sum + (Number(payment.amount || 0) || 0), 0);
  const balance = Math.max(normalizedTotal - totalPaid, 0);
  const paymentMethodOptions = [
    ...paymentMethods,
    ...payments
      .filter((payment) => payment.payment_method_id && !paymentMethods.some((method) => method.id === payment.payment_method_id))
      .map((payment) => ({
        id: payment.payment_method_id,
        name: payment.payment_method_name || 'Método original',
      })),
  ];

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-4" data-testid={`${testIdPrefix}-section`}>
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            <CreditCard className="h-3.5 w-3.5" />
            Pagamentos
          </div>
          <h3 className="mt-2 text-base font-semibold text-slate-950" data-testid={`${testIdPrefix}-title`}>{title}</h3>
          <p className="mt-1 text-xs text-slate-500" data-testid={`${testIdPrefix}-description`}>{description}</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline" data-testid={`${testIdPrefix}-expense-total-badge`}>Despesa: {normalizedTotal.toFixed(2)} MTn</Badge>
          <Badge variant="outline" data-testid={`${testIdPrefix}-paid-total-badge`}>Pagamentos: {totalPaid.toFixed(2)} MTn</Badge>
          <Badge variant="outline" data-testid={`${testIdPrefix}-balance-badge`}>Saldo: {balance.toFixed(2)} MTn</Badge>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid={`${testIdPrefix}-rows`}>
        <div className="min-w-[920px]">
          <div className="grid grid-cols-[minmax(220px,1.35fr)_120px_minmax(180px,1fr)_minmax(180px,1fr)_108px] gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500" data-testid={`${testIdPrefix}-header-row`}>
            <span>Forma de pagamento</span>
            <span>Valor</span>
            <span>Referência</span>
            <span>Notas</span>
            <span></span>
          </div>
          {payments.map((payment, index) => (
            <div key={payment.id || `${testIdPrefix}-${index}`} className="grid grid-cols-[minmax(220px,1.35fr)_120px_minmax(180px,1fr)_minmax(180px,1fr)_108px] gap-3 border-b border-slate-100 px-3 py-2.5 last:border-b-0" data-testid={`${testIdPrefix}-row-${index}`}>
              <Select value={payment.payment_method_id || ''} onValueChange={(value) => onPaymentChange(index, 'payment_method_id', value)}>
                <SelectTrigger className="h-10 rounded-xl border-slate-300 bg-white" data-testid={`${testIdPrefix}-method-${index}`} aria-label={`Forma de pagamento ${index + 1}`}>
                  <SelectValue placeholder="Seleccionar método" />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethodOptions.map((method) => (
                    <SelectItem key={method.id} value={method.id}>{method.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                aria-label={`Valor ${index + 1}`}
                type="number"
                min="0"
                step="0.01"
                value={payment.amount || ''}
                onChange={(event) => onPaymentChange(index, 'amount', event.target.value)}
                className="h-10 rounded-xl border-slate-300 bg-white"
                data-testid={`${testIdPrefix}-amount-${index}`}
              />

              <Input
                aria-label={`Referência ${index + 1}`}
                value={payment.reference || ''}
                onChange={(event) => onPaymentChange(index, 'reference', event.target.value)}
                placeholder="Cheque, M-Pesa, transferência..."
                className="h-10 rounded-xl border-slate-300 bg-white"
                data-testid={`${testIdPrefix}-reference-${index}`}
              />

              <Input
                aria-label={`Notas ${index + 1}`}
                value={payment.notes || ''}
                onChange={(event) => onPaymentChange(index, 'notes', event.target.value)}
                placeholder="Observações opcionais"
                className="h-10 rounded-xl border-slate-300 bg-white"
                data-testid={`${testIdPrefix}-notes-${index}`}
              />

              <div className="flex items-center justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 rounded-xl border-red-200 px-3 text-red-700 hover:bg-red-50"
                  onClick={() => onRemovePayment(index)}
                  disabled={payments.length <= 1}
                  data-testid={`${testIdPrefix}-remove-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500" data-testid={`${testIdPrefix}-hint`}>
          Pode deixar as linhas vazias se a despesa ainda não tiver sido liquidada.
        </p>
        <Button type="button" variant="outline" className="h-10 rounded-xl" onClick={onAddPayment} data-testid={`${testIdPrefix}-add-button`}>
          <Plus className="mr-2 h-4 w-4" />
          Adicionar método
        </Button>
      </div>
    </section>
  );
};